import { Controller } from "@hotwired/stimulus"

/**
 * Notification Type Toggle Controller
 *
 * Auto-saves a NotificationType row when one of its checkboxes is toggled.
 *
 * Usage:
 *   <tr data-controller="notification-type-toggle"
 *       data-notification-type-toggle-url-value="/admin/notifications/types/1">
 *     <input type="checkbox"
 *            data-action="change->notification-type-toggle#save"
 *            data-field="active">
 *     <input type="checkbox"
 *            data-action="change->notification-type-toggle#save"
 *            data-field="default_channels.in_app">
 *   </tr>
 *
 * For nested `default_channels.*` fields, all sibling default_channels checkboxes
 * in the same row are submitted together so the jsonb column isn't partially overwritten.
 */
export default class extends Controller {
  static values = { url: String }

  async save(event) {
    const input = event.currentTarget
    const field = input.dataset.field
    if (!field || !this.hasUrlValue) return

    input.disabled = true

    const formData = new FormData()
    formData.append("_method", "PATCH")

    if (field.startsWith("default_channels.")) {
      const channels = this.element.querySelectorAll('input[data-field^="default_channels."]')
      channels.forEach(cb => {
        const [, child] = cb.dataset.field.split(".")
        formData.append(`notification_type[default_channels][${child}]`, cb.checked ? "1" : "0")
      })
    } else {
      formData.append(`notification_type[${field}]`, input.checked ? "1" : "0")
    }

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        },
        credentials: "same-origin"
      })

      if (!response.ok) {
        this.revert(input)
        const message = await this.errorMessage(response)
        alert(`Failed to update: ${message}`)
      }
    } catch (error) {
      console.error("Error updating notification type:", error)
      this.revert(input)
      alert("An error occurred. Please try again.")
    } finally {
      input.disabled = false
    }
  }

  revert(input) {
    input.checked = !input.checked
  }

  async errorMessage(response) {
    try {
      const data = await response.json()
      if (data?.errors?.length) return data.errors.join(", ")
    } catch (_) {
      // fall through
    }
    return `HTTP ${response.status}`
  }
}
